import {
  Card,
  CardContent,
  Avatar,
  useTheme,
  Grid,
  Button,
  TextField,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useApiClientQuery } from "../../hooks/useApiClient";
import { useContext, useEffect, useRef, useState } from "react";
import api_endpoints from "../../api";
import AuthContext from "../../context/AuthContext/AuthContext";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import NoElements from "../../widgets/NoElements/NoElements";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SearchIcon from "@mui/icons-material/Search";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { Link } from "react-router-dom";
import TableVirtuoso from "../../widgets/TableVirtuoso/TableVirtuoso";
import DonationImportation from "../DonationImportation/DonationImportation";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import ReceiptCampaignDialog from "./ReceiptCampaignDialog";
import dayjs from "dayjs";

const Donors = () => {
  const authContext = useContext(AuthContext);
  const [searchFilter, setSearchFilter] = useState({ search: "", sort: 1 });
  const theme = useTheme();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [generateReceiptDialogOpen, setGenerateReceiptDialogOpen] =
    useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [receiptStartDate, setReceiptStartDate] = useState(
    dayjs().startOf("year")
  );
  const [receiptEndDate, setReceiptEndDate] = useState(dayjs().endOf("year"));
  const [receiptCampaignSearch, setReceiptCampaignSearch] = useState("");
  const [selectedReceiptCampaignIds, setSelectedReceiptCampaignIds] =
    useState([]);
  const [receiptCampaignSelectionError, setReceiptCampaignSelectionError] =
    useState("");
  const [donorsReceiptStatus, setDonorsReceiptStatus] = useState([]);
  const [receiptsDone, setReceiptsDone] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { t } = useCustomTranslation("Donors");
  const organizationId = authContext?.authContext?.accessToken?.organization_id;
  const lastAutoSelectedCampaignKeyRef = useRef(null);
  const receiptCampaignSelectionKey = [
    organizationId || "",
    selectedYear,
    receiptStartDate?.format("YYYY-MM-DD") || "",
    receiptEndDate?.format("YYYY-MM-DD") || "",
  ].join(":");

  useEffect(() => {
    createYearOptionsReceipt();
  }, []);

  const donorsQuery = useApiClientQuery(
    () =>
      api_endpoints.getDonors(
        searchFilter.search,
        searchFilter.sort,
        authContext.authContext.accessToken.organization_id
      ),
    {
      queryKey: [
        "getDonors",
        authContext.authContext.accessToken.organization_id,
        searchFilter,
      ],
    },
    (data) => {
      if (data && data.length > 0) {
        initDonorsReceiptStatus(data);
      }
    }
  );

  const receiptCampaignOptionsQuery = useApiClientQuery(
    () =>
      api_endpoints.getReceiptCampaigns(
        undefined,
        organizationId,
        selectedYear,
        receiptStartDate?.startOf("day").toISOString(),
        receiptEndDate?.endOf("day").toISOString()
      ),
    {
      queryKey: [
        "bulk_donor_receipt_campaigns",
        organizationId,
        selectedYear,
        receiptStartDate?.format("YYYY-MM-DD"),
        receiptEndDate?.format("YYYY-MM-DD"),
      ],
      enabled:
        confirmationDialogOpen &&
        !!organizationId &&
        !!receiptStartDate &&
        !!receiptEndDate &&
        receiptStartDate.isValid() &&
        receiptEndDate.isValid() &&
        !receiptEndDate.isBefore(receiptStartDate),
    }
  );

  useEffect(() => {
    if (!confirmationDialogOpen) {
      lastAutoSelectedCampaignKeyRef.current = null;
      return;
    }

    if (
      !receiptCampaignOptionsQuery.data ||
      lastAutoSelectedCampaignKeyRef.current === receiptCampaignSelectionKey
    ) {
      return;
    }

    setSelectedReceiptCampaignIds(
      receiptCampaignOptionsQuery.data.map((campaign) => campaign._id)
    );
    setReceiptCampaignSelectionError("");
    lastAutoSelectedCampaignKeyRef.current = receiptCampaignSelectionKey;
  }, [
    confirmationDialogOpen,
    receiptCampaignOptionsQuery.data,
    receiptCampaignSelectionKey,
  ]);

  const initDonorsReceiptStatus = (data) => {
    if (!data || data.length === 0) return;

    setDonorsReceiptStatus([
      ...data.map((donor, index) => ({
        donorId: donor?._id ? String(donor._id) : `unknown_donor_${index}`,
        email: donor?.email || `unknown_email_${index}`,
        status: <CircularProgress size="20px" />,
        reason: "",
      })),
    ]);
  };

  const createYearOptionsReceipt = () => {
    let data = [];
    const minYear = 2024;
    let yearOptions = Number(new Date().getFullYear());
    const iterations = yearOptions - minYear;

    for (let i = 0; i <= iterations; i++) {
      data.push(yearOptions--);
    }

    setYearOptions(data);
  };

  const handleReceiptYearChange = (year) => {
    const numericYear = Number(year);
    setSelectedYear(numericYear);
    setReceiptStartDate(dayjs(`${numericYear}-01-01`).startOf("day"));
    setReceiptEndDate(dayjs(`${numericYear}-12-31`).endOf("day"));
    setReceiptCampaignSelectionError("");
  };

  const handleFilterChange = (key, value) => {
    setSearchFilter({ ...searchFilter, [key]: value });
  };

  const clearFilters = () => {
    setSearchFilter({ search: "", sort: 1 });
  };

  const onSortChange = (value) => {
    setSearchFilter({ ...searchFilter, sort: value });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    setReceiptCampaignSelectionError("");
  };

  const openConfirmationDialog = () => {
    setReceiptCampaignSearch("");
    setReceiptCampaignSelectionError("");
    setConfirmationDialogOpen(true);
  };

  const openGenerateReceiptDialog = () => {
    setReceiptsDone(false);
    initDonorsReceiptStatus(donorsQuery.data ?? []);
    setGenerateReceiptDialogOpen(true);
    generateDonorsReceipt();
  };

  const closeGenerateReceiptDialog = () => {
    setGenerateReceiptDialogOpen(false);
  };

  const handleToggleReceiptCampaign = (campaignId) => {
    setReceiptCampaignSelectionError("");
    setSelectedReceiptCampaignIds((current) =>
      current.includes(campaignId)
        ? current.filter((id) => id !== campaignId)
        : [...current, campaignId]
    );
  };

  const handleSelectAllReceiptCampaigns = () => {
    setReceiptCampaignSelectionError("");
    setSelectedReceiptCampaignIds(
      (receiptCampaignOptionsQuery.data || []).map((campaign) => campaign._id)
    );
  };

  const handleClearReceiptCampaigns = () => {
    setSelectedReceiptCampaignIds([]);
  };

  const handleGenerateBulkReceipts = () => {
    if (receiptCampaignOptionsQuery.isFetching) {
      return;
    }

    if (selectedReceiptCampaignIds.length === 0) {
      setReceiptCampaignSelectionError(
        t("donor_receipt_campaign_selection_error_required", {
          fallback: "Select at least one campaign before generating receipts.",
        })
      );
      return;
    }

    if (
      !receiptStartDate ||
      !receiptEndDate ||
      !receiptStartDate.isValid() ||
      !receiptEndDate.isValid() ||
      receiptEndDate.isBefore(receiptStartDate)
    ) {
      setReceiptCampaignSelectionError(
        t("donor_receipt_period_error", {
          fallback: "Select a valid receipt period.",
        })
      );
      return;
    }

    setConfirmationDialogOpen(false);
    openGenerateReceiptDialog();
  };

  const openImportDialog = () => {
    setImportDialogOpen(true);
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
  };

  const getBulkReceiptFailureReason = (error) => {
    const errorKey =
      typeof error?.response?.data === "string"
        ? error.response.data
        : error?.response?.data?.message || error?.message || "";

    const reasons = {
      receipt_no_transactions_for_campaigns_year: {
        status: "skipped",
        text: t("donor_bulk_receipt_reason_no_transactions", {
          defaultValue:
            "No matching donations were found for the selected campaigns and date range.",
        }),
      },
      receipt_donor_email_required: {
        status: "error",
        text: t("donor_bulk_receipt_reason_missing_email", {
          defaultValue: "The donor does not have an email address.",
        }),
      },
      receipt_donor_not_found: {
        status: "error",
        text: t("donor_bulk_receipt_reason_donor_not_found", {
          defaultValue: "The donor profile could not be found.",
        }),
      },
      receipt_organization_not_found: {
        status: "error",
        text: t("donor_bulk_receipt_reason_organization_not_found", {
          defaultValue: "The organization could not be found.",
        }),
      },
      receipt_invalid_campaign_selection: {
        status: "error",
        text: t("donor_bulk_receipt_reason_invalid_campaigns", {
          defaultValue: "The selected campaigns are invalid.",
        }),
      },
      receipt_campaign_selection_required: {
        status: "error",
        text: t("donor_bulk_receipt_reason_no_campaign", {
          defaultValue: "No campaign was selected.",
        }),
      },
      receipt_invalid_period: {
        status: "error",
        text: t("donor_bulk_receipt_reason_invalid_period", {
          defaultValue: "The selected receipt period is invalid.",
        }),
      },
      receipt_period_both_dates_required: {
        status: "error",
        text: t("donor_bulk_receipt_reason_invalid_period", {
          defaultValue: "The selected receipt period is invalid.",
        }),
      },
      receipt_unauthorized: {
        status: "error",
        text: t("donor_bulk_receipt_reason_unauthorized", {
          defaultValue: "You are not authorized to generate this receipt.",
        }),
      },
      receipt_invalid_input: {
        status: "error",
        text: t("donor_bulk_receipt_reason_invalid_input", {
          defaultValue: "The receipt data is invalid.",
        }),
      },
    };

    return (
      reasons[errorKey] || {
        status: "error",
        text: t("donor_bulk_receipt_reason_generic", {
          defaultValue:
            "An unexpected error occurred while generating the receipt.",
        }),
      }
    );
  };

  const handleDonorsReceiptStatusChange = (status, donorId, reason = "") => {
    const statusLabels = {
      success: (
        <div style={{ color: theme.palette.success.main, fontWeight: 600 }}>
          {t("donor_bulk_receipt_status_success", { defaultValue: "Success" })}
        </div>
      ),
      skipped: (
        <div style={{ color: theme.palette.warning.main, fontWeight: 600 }}>
          {t("donor_bulk_receipt_status_skipped", { defaultValue: "Skipped" })}
        </div>
      ),
      error: (
        <div style={{ color: theme.palette.error.main, fontWeight: 600 }}>
          {t("donor_bulk_receipt_status_error", { defaultValue: "Error" })}
        </div>
      ),
    };

    setDonorsReceiptStatus((currentStatuses) =>
      currentStatuses.map((donor) =>
        donor.donorId === String(donorId)
          ? {
              ...donor,
              status: statusLabels[status] || statusLabels.error,
              reason,
            }
          : donor
      )
    );
  };

  const generateDonorsReceipt = async () => {
    const orgId = organizationId;

    for (const [index, donor] of (donorsQuery.data ?? []).entries()) {
      const donorId = donor?._id;
      const statusDonorId = donorId
        ? String(donorId)
        : `unknown_donor_${index}`;

      if (!donorId || !orgId) {
        handleDonorsReceiptStatusChange(
          "error",
          statusDonorId,
          t("donor_bulk_receipt_reason_invalid_input", {
            defaultValue: "The receipt data is invalid.",
          })
        );
        continue;
      }

      try {
        const payload = {
          donor_id: String(donorId),
          organization_id: String(orgId),
          year: Number(selectedYear),
          campaign_ids: selectedReceiptCampaignIds,
          start_date: receiptStartDate.startOf("day").toISOString(),
          end_date: receiptEndDate.endOf("day").toISOString(),
        };

        const res = await api_endpoints.generateReceipt(payload, {
          skipGlobalErrorAlert: true,
        });

        if (res.status === 200) {
          handleDonorsReceiptStatusChange("success", statusDonorId);
        } else {
          handleDonorsReceiptStatusChange(
            "error",
            statusDonorId,
            t("donor_bulk_receipt_reason_generic", {
              defaultValue:
                "An unexpected error occurred while generating the receipt.",
            })
          );
        }
      } catch (e) {
        const failure = getBulkReceiptFailureReason(e);
        handleDonorsReceiptStatusChange(
          failure.status,
          statusDonorId,
          failure.text
        );
        if (failure.status === "error") {
          console.error(e);
        }
      }
    }
    setReceiptsDone(true);
  };


  return (
    <>
      <Dialog
        open={generateReceiptDialogOpen}
        onClose={closeGenerateReceiptDialog}
        fullScreen
      >
        <DialogTitle>{t("donor_page_generate_tax_popup_title")}</DialogTitle>
        <DialogContent>
          <div className="flex flex-1 flex-col h-full">
            <div>
              {t("donor_page_generate_tax_popup_message")}
              <span className="font-semibold"> {selectedYear}</span>
            </div>
            <div className="flex flex-1 h-full mt-4">
              <TableVirtuoso
                rows={donorsReceiptStatus}
                columns={[
                  {
                    key: "email",
                    label: t("donor_bulk_receipt_table_donor", {
                      defaultValue: "Donor",
                    }),
                  },
                  {
                    key: "status",
                    label: t("donor_bulk_receipt_table_status", {
                      defaultValue: "Status",
                    }),
                  },
                  {
                    key: "reason",
                    label: t("donor_bulk_receipt_table_details", {
                      defaultValue: "Details",
                    }),
                  },
                ]}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={!receiptsDone}
            onClick={closeGenerateReceiptDialog}
          >
            {t("donor_page_generate_tax_popup_close_text")}
          </Button>
        </DialogActions>
      </Dialog>
      <ReceiptCampaignDialog
        t={t}
        open={confirmationDialogOpen}
        onClose={closeConfirmationDialog}
        isGenerating={false}
        isLoading={receiptCampaignOptionsQuery.isLoading}
        isActionDisabled={receiptCampaignOptionsQuery.isFetching}
        isError={receiptCampaignOptionsQuery.isError}
        campaigns={receiptCampaignOptionsQuery.data || []}
        search={receiptCampaignSearch}
        onSearchChange={setReceiptCampaignSearch}
        selectedCampaignIds={selectedReceiptCampaignIds}
        onSelectAll={handleSelectAllReceiptCampaigns}
        onClearAll={handleClearReceiptCampaigns}
        onToggleCampaign={handleToggleReceiptCampaign}
        selectionError={receiptCampaignSelectionError}
        selectedYear={selectedYear}
        startDate={receiptStartDate}
        endDate={receiptEndDate}
        onStartDateChange={setReceiptStartDate}
        onEndDateChange={setReceiptEndDate}
        onGenerate={handleGenerateBulkReceipts}
        title={t("donor_page_generate_tax_popup_title")}
        yearOptions={yearOptions}
        onYearChange={handleReceiptYearChange}
        yearPrompt={t("donor_page_generate_tax_forall_donors_popup_message")}
        actionLabel={t(
          "donor_page_generate_tax_forall_donors_popup_generate_button"
        )}
        warningText={t("donor_page_generate_tax_forall_donors_popup_guide")}
      />
      <DonationImportation
        open={importDialogOpen}
        onClose={closeImportDialog}
        onOpen={openImportDialog}
      />
      <div className="flex justify-end space-x-4">
        <Button variant="contained" onClick={openImportDialog}>
          {t("donor_page_top_import_button")}
        </Button>
        <Button variant="contained" onClick={openConfirmationDialog}>
          {t("donor_page_top_generate_button")}
        </Button>
      </div>
      <div className="container-main !flex-row [&>div]:flex space-x-4 items-end">
        <div className="flex-1 flex-col">
          {t("donor_page_search_filter")}
          <TextField
            placeholder={t("donor_page_search_filter")}
            size="small"
            InputProps={{ startAdornment: <SearchIcon className="mr-2" /> }}
            value={searchFilter.search}
            onInput={(e) => handleFilterChange("search", e.target.value)}
          />
        </div>
        <div className="flex-col">
          {t("donor_page_sort_by_filter")}
          <Select
            size="small"
            value={searchFilter.sort}
            onChange={(e) => onSortChange(e.target.value)}
          >
            <MenuItem value={1}>
              <NorthIcon />
              {t("donor_page_sort_by_asc")}
            </MenuItem>
            <MenuItem value={-1}>
              <SouthIcon /> {t("donor_page_sort_by_desc")}
            </MenuItem>
          </Select>
        </div>
        <div className="space-x-4">
          <Button color="secondary" onClick={clearFilters}>
            {t("donor_page_clear_filter_text")}
          </Button>
        </div>
      </div>
      <LoadingContainer
        loading={donorsQuery.isLoading}
        error={donorsQuery.isError}
      >
        <NoElements
          elements={donorsQuery.data}
          noElementsText={t("donor_page_nodonor_available_text")}
          onRefresh={donorsQuery.refetch}
        >
          <Grid container spacing={2}>
            {donorsQuery.data &&
              donorsQuery.data.map((donor, index) => (
                <Grid
                  item
                  key={donor?._id || `donor_${index}`}
                  xs={12}
                  sm={12}
                  md={12}
                  lg={6}
                  xl={4}
                >
                  <Card className="flex h-[120px] w-full">
                    <MenuItem className="w-full h-full">
                      <Link
                        to={`/donors/${encodeURIComponent(String(donor?.email ?? "").trim())}`}
                        className="flex w-full h-full"
                      >
                        <CardContent className="flex flex-row items-center space-x-4">
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.info.main,
                            }}
                          >
                            {(donor?.email || 'U')[0].toUpperCase()}
                          </Avatar>
                          <div className="flex flex-1 flex-col [&>label]:text-lg [&>span]:text-gray-500">
                            <label>
                              <EmailOutlinedIcon /> {donor?.email}
                            </label>
                            {donor?.first_name ? (
                              <span>
                                <PersonOutlinedIcon fontSize={"small"} />
                                {donor?.first_name} {donor?.last_name}
                              </span>
                            ) : (
                              <></>
                            )}
                            {donor?.phone ? (
                              <span>
                                <PhoneOutlinedIcon fontSize={"small"} />{" "}
                                {donor?.phone}
                              </span>
                            ) : (
                              <></>
                            )}
                          </div>
                        </CardContent>
                      </Link>
                    </MenuItem>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </NoElements>
      </LoadingContainer>
    </>
  );
};

export default Donors;
